import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, MessageCircle, Trash2, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import AppointmentDialog from "@/components/AppointmentDialog";
import { toast } from "sonner";

const HOURS = Array.from({ length: 11 }, (_, i) => i + 9); // 9am - 7pm
const DAY_NAMES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const TIMEZONE = "America/Santiago";

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getChileDateKey(date) {
  return date.toLocaleDateString("en-CA", { timeZone: TIMEZONE });
}

export default function CalendarPage() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultStart, setDefaultStart] = useState(null);

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/appointments"), api.get("/patients")]);
    setAppointments(a.data);
    setPatients(p.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const apptsByDay = days.map((d) => {
    const dayKey = getChileDateKey(d);
    return appointments.filter((a) => getChileDateKey(new Date(a.start)) === dayKey);
  });

  const handleSlotClick = (day, hour) => {
    const start = new Date(day);
    start.setHours(hour, 0, 0, 0);
    setDefaultStart(start);
    setEditing(null);
    setShowDialog(true);
  };

  const handleApptClick = (a) => {
    setEditing(a);
    setDefaultStart(null);
    setShowDialog(true);
  };

  const sendReminder = async (id, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/appointments/${id}/send-reminder`);
      if (data.whatsapp_response?.status_code === 200) {
        toast.success("Recordatorio enviado");
      } else {
        toast.warning("Llamada realizada. Verifica respuesta de WhatsApp.");
        console.log(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el recordatorio");
    }
  };

  const deleteAppt = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta cita?")) return;
    await api.delete(`/appointments/${id}`);
    toast.success("Cita eliminada");
    load();
  };

  const downloadIcs = async () => {
    const token = localStorage.getItem("agenda_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/export.ics?token=${token}`, "_blank");
  };

  return (
    <div className="p-8 lg:p-12">
      <header className="flex items-end justify-between mb-8">
        <div>
          <span className="label-eyebrow text-[#B5704A]">Agenda</span>
          <h1 className="font-display text-3xl sm:text-4xl font-[350] text-[#3A2418] mt-2">
            Calendario <span className="italic font-[700]">semanal</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadIcs} className="rounded-full border-[#E8DFD2]" data-testid="export-ics">
            <CalendarDays className="w-4 h-4 mr-2" /> Exportar .ics
          </Button>
          <Button onClick={() => { setEditing(null); setDefaultStart(null); setShowDialog(true); }} className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full" data-testid="new-appointment-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva cita
          </Button>
        </div>
      </header>

      {/* Week nav */}
      <div className="surface-card p-4 mb-4 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
          data-testid="prev-week"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="font-display text-lg text-[#3A2418]">
          {weekStart.toLocaleDateString("es", { day: "numeric", month: "long" })} – {days[6].toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))} className="rounded-full" data-testid="today-btn">Hoy</Button>
          <Button variant="ghost" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }} data-testid="next-week">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="surface-card overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Headers */}
          <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-[#E8DFD2]">
            <div className="p-3 text-xs text-[#6F5A4C]"></div>
            {days.map((d) => {
              const isToday = getChileDateKey(d) === getChileDateKey(new Date());
              return (
                <div key={d.toISOString()} className={`p-3 text-center border-l border-[#E8DFD2] ${isToday ? "bg-[#E8DFD2]/30" : ""}`}>
                  <div className="label-eyebrow text-[#6F5A4C] text-[10px]">{DAY_NAMES[new Date(d.toLocaleString("en-US", { timeZone: TIMEZONE })).getDay()]}</div>
                  <div className={`font-display text-xl mt-1 ${isToday ? "text-[#B5704A] font-[800]" : "text-[#3A2418] font-[600]"}`}>{new Date(d.toLocaleString("en-US", { timeZone: TIMEZONE })).getDate()}</div>
                </div>
              );
            })}
          </div>

          {/* Hours */}
          {HOURS.map((h) => (
            <div key={h} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-[#E8DFD2]/60">
              <div className="p-2 text-xs text-[#6F5A4C] text-right pr-3 pt-1">{h.toString().padStart(2, "0")}:00</div>
              {days.map((d, i) => {
                const appts = apptsByDay[i].filter((a) => new Date(a.start).getHours() === h);
                return (
                  <div
                    key={d.toISOString()}
                    className="border-l border-[#E8DFD2]/60 p-1 min-h-[60px] hover:bg-[#F4EFE4] cursor-pointer transition-colors"
                    onClick={() => handleSlotClick(d, h)}
                    data-testid={`slot-${i}-${h}`}
                  >
                    {appts.map((a) => (
                      <div
                        key={a.id}
                        onClick={(e) => { e.stopPropagation(); handleApptClick(a); }}
                        className="bg-[#B5704A] text-white text-xs p-2 rounded-md mb-1 group cursor-pointer hover:bg-[#9C5E3D] transition-colors"
                        data-testid={`appt-cell-${a.id}`}
                      >
                        <div className="font-medium truncate">{a.title}</div>
                        <div className="text-white/80 text-[10px]">
                          {new Date(a.start).toLocaleTimeString("es-CL", { timeZone: TIMEZONE, hour: "2-digit", minute: "2-digit" })}
                        </div>
                        <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => sendReminder(a.id, e)} className="p-1 hover:bg-white/20 rounded" title="Enviar recordatorio">
                            <MessageCircle className="w-3 h-3" />
                          </button>
                          <button onClick={(e) => deleteAppt(a.id, e)} className="p-1 hover:bg-white/20 rounded" title="Eliminar">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <AppointmentDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSaved={load}
        patients={patients}
        editing={editing}
        defaultStart={defaultStart}
      />
    </div>
  );
}
